'use client';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import Logo from '@/lib/logo/Logo';
import { PanelLeft, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import type React from 'react';

interface SidebarBrandProps {
  showText: boolean;
  className?: string | undefined;
  onToggleCollapse?: (() => void) | undefined;
  showRightIcon?: boolean | undefined;
  // When true (intended for the collapsed rail), render ONLY the expand button
  // in place of the Lexomat logo — there is no brand glyph in the 56px rail
  // (claude.com pattern, #6212-follow-up). The logo lives on the chat surface /
  // header instead. The button expands the sidebar back to its full width.
  expandButtonOnly?: boolean | undefined;
  // When true (mobile view), show a close (X) icon instead of PanelLeft
  useCloseIcon?: boolean | undefined;
}

export const SidebarBrand: React.FC<SidebarBrandProps> = ({
  showText,
  className,
  onToggleCollapse,
  showRightIcon = true,
  expandButtonOnly = false,
  useCloseIcon = false,
}) => {
  const t = useTranslations('SidebarHeader');
  const tCommon = useTranslations('Common');
  return (
    <div
      className={`flex items-center justify-between ${className ?? ''}`.trim()}
    >
      <div className='flex items-center gap-2 relative'>
        {expandButtonOnly && onToggleCollapse ? (
          // Collapsed rail: the brand slot holds ONLY the expand button — no
          // Lexomat logo (the rail is 56px and icon-only). The button keeps a
          // ≥44px square hit area (WCAG 2.1 AA SC 2.5.5) and matches the geometry
          // of the nav icons below it (px-1.5 py-1.5) so the column reads as one
          // stack. It expands the sidebar back to full width.
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleCollapse();
                  }}
                  className='group flex items-center justify-center rounded-md min-h-[44px] min-w-[44px] px-1.5 py-1.5 hover:bg-interactive-hover transition-colors cursor-pointer'
                  aria-label={tCommon('expandSidebar')}
                  // a11y (#6058): tooltip duplicates this aria-label — suppress
                  // the redundant Radix `aria-describedby` so a screen reader
                  // announces "Expand sidebar" once, not twice.
                  aria-describedby={undefined}
                >
                  <PanelLeft size={20} className='text-brand-primary-color' />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side='right'
                align='center'
                sideOffset={8}
                className='z-[2200]'
              >
                <span>{tCommon('expandSidebar')}</span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Link href='/' className='block'>
            <Logo showText={showText} />
          </Link>
        )}
      </div>

      {onToggleCollapse && showRightIcon && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onToggleCollapse}
                className='group flex items-center justify-center min-h-[44px] min-w-[44px] p-1.5 rounded-md hover:bg-interactive-hover transition-colors'
                aria-label={t('collapseSidebar')}
                // a11y (#6058): tooltip duplicates this aria-label — suppress
                // the redundant Radix `aria-describedby` (SR double-announce).
                aria-describedby={undefined}
              >
                {useCloseIcon ? (
                  <X
                    size={20}
                    className='text-brand-primary-color hover:text-brand-primary-color/80'
                  />
                ) : (
                  <PanelLeft size={20} className='text-brand-primary-color' />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent
              side='left'
              align='center'
              sideOffset={8}
              className='z-[2200]'
            >
              <span>{t('collapseSidebar')}</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
